import type { ComponentType } from "react";
import { lazyRetry } from "./lazyRetry";

export type KnowledgePageType = "help-index" | "help-topic" | "help-article" |
  "help-contact" | "help-search" | "guide-index" | "guide-category" | "guide-article";
const loaders: Record<KnowledgePageType, () => Promise<{ default: ComponentType }>> = {
  "help-index": () => import("@/pages/help/HelpPage"),
  "help-topic": () => import("@/pages/help/HelpTopicPage"),
  "help-article": () => import("@/pages/help/HelpArticlePage"),
  "help-contact": () => import("@/pages/help/HelpContactPage"),
  "help-search": () => import("@/pages/help/HelpSearchPage"),
  "guide-index": () => import("@/pages/guides/GuidesIndexPage"),
  "guide-category": () => import("@/pages/guides/GuideCategoryPage"),
  "guide-article": () => import("@/pages/guides/GuideArticlePage"),
};
const pages: Partial<Record<KnowledgePageType, ComponentType>> = {};
const pending: Partial<Record<KnowledgePageType, Promise<void>>> = {};
const lazyPages: Record<KnowledgePageType, ReturnType<typeof lazyRetry>> = {
  "help-index": lazyRetry(loaders["help-index"]),
  "help-topic": lazyRetry(loaders["help-topic"]),
  "help-article": lazyRetry(loaders["help-article"]),
  "help-contact": lazyRetry(loaders["help-contact"]),
  "help-search": lazyRetry(loaders["help-search"]),
  "guide-index": lazyRetry(loaders["guide-index"]),
  "guide-category": lazyRetry(loaders["guide-category"]),
  "guide-article": lazyRetry(loaders["guide-article"]),
};

/** Only immutable component definitions are shared. The request's source DTO
 * stays in SsrRequestContext, never in these module-level loading caches. */
export function loadEagerKnowledgePage(type: KnowledgePageType): Promise<void> {
  if (!pending[type]) pending[type] = loaders[type]().then(module => {
    pages[type] = module.default;
  }).catch(error => {
    delete pending[type];
    throw error;
  });
  return pending[type]!;
}
export function PublicKnowledgeRoute({ type }: { type: KnowledgePageType }) {
  const Page = pages[type] || lazyPages[type];
  return <Page />;
}
